/**
 * RouteErrorBoundary: the calm full screen when a page itself fails to
 * render. Wired as the root route's errorElement, so a render or loader
 * error from any child route shows this instead of the router's raw
 * stack. The four answers come from errorCopy.ts (page_crash); the
 * reference is a code minted here plus the time, so the person can
 * quote it, and the same code is reported to the office's Errors tab,
 * with the status of a route error response. Never a raw message, a
 * status or a stack on screen.
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { useRouteError, isRouteErrorResponse, useParams, Link } from 'react-router-dom';
import { defaultTenant } from '@/lib/auth';
import { errorText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { reportPageCrash } from '@/lib/appErrors';

function mintReference(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID().slice(0, 8);
  return Math.random().toString(16).slice(2, 10);
}

export function RouteErrorBoundary(): ReactNode {
  const error = useRouteError();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const contact = useSupportContact();
  const homeHref = `/t/${tenantSlug ?? defaultTenant}/hub`;
  const reference = useMemo(mintReference, []);
  const at = useMemo(
    () => new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    [],
  );
  const text = errorText('page_crash', 'crashed', contact);

  // Tell the office (ADR-052): the reference on this screen becomes a row
  // the Errors tab can find. Once per crash; the page path, never a query
  // string; the error's own name and message, never what the person typed.
  useEffect(() => {
    const detail =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : isRouteErrorResponse(error)
          ? `${error.status} ${error.statusText}`
          : null;
    reportPageCrash(tenantSlug ?? defaultTenant, {
      reference,
      route: window.location.pathname,
      error_code: 'page_crash',
      detail,
    });
    // The crash is one event; the reference is minted once for it.
  }, [reference]);

  return (
    <div data-testid="route-error-boundary" className="min-h-screen flex items-center justify-center bg-cream px-6">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl font-semibold text-sage-800 mb-2">This page hit a problem</h1>
        <p className="text-sm text-ink-soft mb-6">{text}</p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="route-error-reload"
            className="px-4 py-2 rounded-pill bg-sage-800 text-cream text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            Reload
          </button>
          <Link
            to={homeHref}
            className="px-4 py-2 rounded-pill text-sage-800 text-sm font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            Back to Home
          </Link>
        </div>
        <p className="mt-6 font-mono text-xs text-ink-soft" data-testid="route-error-detail">
          ref {reference} at {at}
        </p>
      </div>
    </div>
  );
}
